import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { motion } from "motion/react";
import { useId, type ComponentProps, type ReactNode, type Ref } from "react";

import { cn } from "@/lib/utils/cn";

type SegmentedControlOption<T extends string> = {
  value: T;
  label: ReactNode;
};

export type SegmentedControlProps<T extends string> = {
  value?: T;
  onValueChange: (value: T) => void;
  options: readonly SegmentedControlOption<T>[];
  label: string;
  disabled?: boolean;
  className?: string;
  rootRef?: Ref<HTMLDivElement>;
  itemClassName?: string;
} & Pick<
  ComponentProps<typeof RadioGroupPrimitive.Root>,
  "aria-invalid" | "aria-describedby"
>;

const INDICATOR_TRANSITION = {
  type: "spring",
  bounce: 0,
  duration: 0.3,
} as const;

export function SegmentedControl<T extends string>({
  value,
  onValueChange,
  options,
  label,
  disabled = false,
  className,
  rootRef,
  itemClassName,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
}: SegmentedControlProps<T>) {
  const indicatorId = useId();

  const handleValueChange = (next: string) => {
    const option = options.find((candidate) => candidate.value === next);
    if (!option) return;
    onValueChange(option.value);
  };

  return (
    <RadioGroupPrimitive.Root
      ref={rootRef}
      value={value ?? ""}
      onValueChange={handleValueChange}
      aria-label={label}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
      disabled={disabled}
      orientation="horizontal"
      className={cn(
        "inline-flex items-center gap-0.5 rounded-lg border border-border bg-transparent p-1",
        className,
      )}
    >
      {options.map((option) => {
        const isSelected = option.value === value;
        return (
          <RadioGroupPrimitive.Item
            key={option.value}
            value={option.value}
            className={cn(
              "relative h-11 min-w-12 rounded-md px-3 md:h-6.5 text-sm font-semibold outline-none transition-colors",
              "focus-visible:ring-ring/50 focus-visible:ring-[3px]",
              "disabled:cursor-default",
              isSelected
                ? "text-primary-foreground"
                : "cursor-pointer text-muted-foreground hover-tint-primary",
              itemClassName,
            )}
          >
            {isSelected && (
              <motion.span
                layoutId={indicatorId}
                transition={INDICATOR_TRANSITION}
                className="absolute inset-0 rounded-md bg-primary"
              />
            )}
            <span className="relative">{option.label}</span>
          </RadioGroupPrimitive.Item>
        );
      })}
    </RadioGroupPrimitive.Root>
  );
}
